/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { h } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { LABEL_ECO_QUALITY } from '../../../../../core/quality/types';
import {
  USER_CLICK,
  USER_ECO_CLOSED,
  USER_ECO_OPENED,
  USER_ECO_TOGGLED,
  USER_QUALITY_CHANGED
} from '../../../../../types';
import { useNewStore, usePlayerContext, useStyles } from '../../../../common/hooks';
import { withBreakPoints } from '../../../hoc';
import { Button, ToggleButton } from '../../common';
import { Modal } from '../../common/modal';

import { PIANO_VALUE_POSITION_ECO } from '../../../../../monitoring/piano/types';
import { IconContext } from '../../../../common/components/svg/icons/icon-context';
import { MAX_LAYER } from '../../../../common/constants';
import { useOpenDialogsCounter, useRegisterDialog } from '../../../../common/context/DialogContext';
import Scrollable from '../../../../common/hoc/scrollable';
import { TEXT_LEVEL_1_FONT_SIZE, TEXT_LEVEL_2_FONT_SIZE, TEXT_LEVEL_3_FONT_SIZE } from '../../../../common/styles';
import { FONT_FAMILY_BROWN_WITH_FALLBACKS } from '../../../../fonts';
import { LABELS_FILL_COLOR, PRIMARY_FILL_COLOR, PRIMARY_TEXT_COLOR, SEPARATOR_COLOR } from '../../../theme/colors';
import '../../../theme/styles/eco.css';
import { handleCloseModalButton } from '../../../utils/modal';
import { TOGGLE_LABEL_POSITIONS } from '../../common/buttons/ToggleButton';
import { SLIDE_MENU_ITEMS_SPACING } from '../../common/menu/styles';
import { MODAL_BASIC_STYLE, MODAL_CLOSING_BUTTON_STYLE } from '../../common/modal/types';
import { ModalWrapper } from '../../common/modal/wrapper';
import { ECO_STYLES_FOOTER } from './styles';
import { ECO_DEFAULT_TEXTS, ECO_MOBILE_TEXTS } from './types';

function EcoButton({
  mobile,
  tablet,
  small,
  size,
  hidden
}) {
  const player = usePlayerContext();
  const { controlIconSize } = useNewStore('ui', player);
  const { selectedQualityLevel, ecoQuality, currentQualityLabel } = useNewStore('playback', player);
  const Icon = useContext(IconContext);
  const [isOpened, setIsOpened] = useState(false);
  const [isOutsideModal, setIsOutsideModal] = useState(null);
  const [isFocusable, setIsFocusable] = useState(true);
  const [isEco, setIsEco] = useState(selectedQualityLevel === ecoQuality?.level);
  const closeIconRef = useRef(null);
  const iconRef = useRef(null);
  const wrapperRef = useRef(null);
  const setStyles = useStyles();

  const { title, title2, body1, body2, footer } = mobile || tablet ? ECO_MOBILE_TEXTS : ECO_DEFAULT_TEXTS;

  const setEcoMode = ({ action, source }) => {
    const { bitrate, resolution, level, label } = !isEco
      ? ecoQuality
      : { bitrate: 0, resolution: 0, level: -1, label: 'automatique' };

    player.setVideoQuality({ bitrate, resolution });
    player.userEvents$.next({ action, source, value: !isEco });
    player.userEvents$.next({
      action: USER_QUALITY_CHANGED,
      source,
      value: {
        position: PIANO_VALUE_POSITION_ECO,
        previousLabel: currentQualityLabel,
        newLabel: label,
        level,
        bitrate,
        resolution
      }
    });
    setIsEco(!isEco);
  };

  useRegisterDialog({
    id: 'eco',
    openingMode: 'click',
    state: [isOpened, setIsOpened]
  });
  useOpenDialogsCounter(isOpened);
  useEffect(() => setIsEco(selectedQualityLevel === ecoQuality?.level), [selectedQualityLevel, ecoQuality?.level]);

  const ecoModeActivated = currentQualityLabel === LABEL_ECO_QUALITY || isEco || selectedQualityLevel === ecoQuality?.level;
  const focusableItem = isFocusable ? 'ftv-magneto--focusable-item' : '';

  useEffect(() => {
    if (isOpened && closeIconRef.current) {
      setIsOutsideModal(false);
      closeIconRef.current.focus();
    }
  }, [isOpened, isOutsideModal]);

  const handleCloseModal = (event) => {
    handleCloseModalButton({
      event,
      player,
      currentIconRef: iconRef.current,
      setIsModalOpened: setIsOpened,
      closedAction: USER_ECO_CLOSED
    });
  };

  return !hidden && (
    <ModalWrapper wrapperCssClasses="ftv-magneto--glass-wrapper">
      <Button
        enableBackground
        hidden={hidden}
        type="icon"
        openingMode="click"
        label="Mode éco"
        xs={small}
        ariaLabel="Mode éco"
        forceLabel
        name="btn-eco"
        onClick={() => {
          player.userEvents$.next({
            action: USER_ECO_OPENED,
            value: ecoModeActivated,
            source: USER_CLICK
          });
          setIsOpened(!isOpened);
        }}
        size={size}
        icon={ecoModeActivated ? 'eco-activated' : 'eco'}
        iconRef={iconRef}
        disableFocus={isOpened}
        disableCloseFocus
        ariaHidden={isOpened}
        ariaProps={{
          'aria-haspopup': 'dialog',
          'aria-controls': 'eco-modal',
          'aria-expanded': isOpened
        }}
        zone="top"
      />

      {isOpened && (
        <Modal
          trap
          id="eco-modal"
          show={isOpened}
          attachedTo="btn-eco"
          styles={MODAL_BASIC_STYLE}
          onChange={(openedState) => {
            setIsOutsideModal(true);
            setIsOpened(openedState);
          }}
          onEscapeKeyDown={handleCloseModal}
          closingButtonStyle={setStyles(MODAL_CLOSING_BUTTON_STYLE)}
          closeComponent={(
            <Button
              type="icon"
              openingMode="click"
              label="Fermer"
              ariaLabel="Fermer la fenêtre Mode éco et retourner au lecteur vidéo"
              forceLabel
              disableFocus={false}
              last
              zone="bottom"
              onClick={handleCloseModal}
              size={controlIconSize}
              icon="close"
              xs
              iconRef={closeIconRef}
              name="btn-close-eco"
              ariaHidden={false}
              // Fix close button not accessible in fullscreen modal
              style={{ position: 'relative', zIndex: MAX_LAYER }}
            />
          )}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            ref={wrapperRef}
            className="ftv-magneto--eco-wrapper"
            style={setStyles({
              default: { padding: '20px 0 20px 20px', color: LABELS_FILL_COLOR, fontFamily: FONT_FAMILY_BROWN_WITH_FALLBACKS },
              'small+medium': { padding: '18px 0 18px 18px' }
            })}
            onKeyDown={() => setIsFocusable(true)}
            onClick={() => setIsFocusable(false)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div
              className={`ftv-magneto--eco-header ${focusableItem}`}
              role="heading"
              aria-level="2"
              style={setStyles({ default: { gap: 10 } })}
            >
              <Icon
                width={controlIconSize}
                height={controlIconSize}
                color={PRIMARY_FILL_COLOR}
                name={ecoModeActivated ? 'eco-activated' : 'eco'}
              />

              <div
                style={{
                  ...setStyles(TEXT_LEVEL_1_FONT_SIZE),
                  color: PRIMARY_FILL_COLOR
                }}
              >
                {title}
              </div>
            </div>

            <div className={`ftv-magneto--eco-status ${focusableItem}`} style={{ marginLeft: -12 }}>
              <ToggleButton
                val={ecoModeActivated}
                onToggle={() => {
                  setEcoMode({
                    action: USER_ECO_TOGGLED,
                    source: USER_CLICK
                  });
                }}
                label={`${title} (${ecoModeActivated ? 'activé' : 'désactivé'})`}
                labelPosition={TOGGLE_LABEL_POSITIONS.BEFORE}
                labelStyles={{ marginRight: 'auto', fontWeight: 700 }}
                wrapperStyles={{
                  ...SLIDE_MENU_ITEMS_SPACING,
                  margin: '10px 12px 10px 0px'
                }}
              />
            </div>

            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div className="ftv-magneto--eco-body">
              <Scrollable>
                <div
                  style={{ paddingRight: '24px' }}
                >
                  <p
                    className={`ftv-magneto--eco-text-1 ${focusableItem}`}
                    style={{
                      color: PRIMARY_TEXT_COLOR,
                      ...setStyles(TEXT_LEVEL_2_FONT_SIZE),
                      marginTop: 0
                    }}
                  >
                    {body1}
                  </p>
                  <div
                    className={`ftv-magneto--separator-line ${focusableItem}`}
                    style={{ backgroundColor: SEPARATOR_COLOR }}
                  />

                  <div
                    className={`ftv-magneto--eco-text-2-title ${focusableItem}`}
                    role="heading"
                    aria-level="3"
                    style={{
                      color: PRIMARY_TEXT_COLOR,
                      ...setStyles(TEXT_LEVEL_2_FONT_SIZE),
                      fontWeight: 700
                    }}
                  >
                    {title2}
                  </div>

                  <p
                    className={`ftv-magneto--eco-text-2-body ${focusableItem}`}
                    style={{
                      color: PRIMARY_TEXT_COLOR,
                      ...setStyles(TEXT_LEVEL_2_FONT_SIZE),
                      marginTop: 0
                    }}
                  >
                    {body2}
                  </p>

                  <p
                    className={`ftv-magneto--eco-text-2-footer ${focusableItem}`}
                    style={{
                      ...setStyles(TEXT_LEVEL_3_FONT_SIZE),
                      ...ECO_STYLES_FOOTER
                    }}
                  >
                    {footer}
                  </p>
                </div>
              </Scrollable>
            </div>
          </div>
        </Modal>
      )}
    </ModalWrapper>
  );
}

export default withBreakPoints(EcoButton);
